import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useAuth } from "../../context/AuthContext";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { EMPLOYEES_BY_AGENCY_ID } from "../../api/query";
import { CREATE_ENHANCEMENT } from "../../api/mutation";
import {
  showError,
  showSuccess,
} from "../../components/common/ToastNotification";
import BreadCumb from "../../components/common/Breadcrumb";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import InputsNew from "../../components/common/InputsNew";
import backArrow from "../../assets/task/back_arrow.svg";
import Loader from "../../components/common/Loader";
import DateSelector from "../../components/common/DateSelector";
import { getSupabaseExt } from "../../lib/supabase";

const platformOptions = [
  { key: "Web", name: "Web" },
  { key: "Mobile", name: "Mobile" },
  { key: "API", name: "API" },
  { key: "Other", name: "Other" },
];

const priorityOptions = [
  { key: "Low", name: "Low" },
  { key: "Medium", name: "Medium" },
  { key: "High", name: "High" },
  { key: "Critical", name: "Critical" },
];

const enhancementStatusOptions = [
  { key: "Open", name: "Open" },
  { key: "In Progress", name: "In Progress" },
  { key: "Resolved", name: "Resolved" },
  { key: "Closed", name: "Closed" },
];

interface AddEnhancementFormData {
  enhancement_reported_on: string;
  reported_by_name: string;
  reported_by_id: string;
  agency_name: string;
  platform: string;
  other_platform?: string;
  priority: string;
  enhancement_details: string;
  enhance_assigned_name: string;
  enhance_assigned_id: string;
  enhancement_status: string;
  release_date?: string;
  work_started_on?:string;
  remarks?: string;
}

const addEnhancementSchema = yup.object().shape({
  enhancement_reported_on: yup.string().required("This field is required"),
  reported_by_name: yup.string().required("This field is required"),
  reported_by_id: yup.string().required("This field is required"),
  agency_name: yup
    .string()
    .test(
      "not-only-whitespace",
      "Please add a valid Agency Name",
      (value) => !value || value.trim().length > 0,
    )
    .required("This field is required"),
  platform: yup.string().required("This field is required"),
  other_platform: yup.string().when("platform", {
    is: "Other",
    then: (schema) =>
      schema
        .trim()
        .required("Please enter Platform")
        .test(
          "not-only-whitespace",
          "Please enter valid Platform",
          (value) => !!value?.trim(),
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  priority: yup.string().required("This field is required"),
  enhancement_details: yup
    .string()
    .test(
      "not-only-whitespace",
      "Please add valid Enhancements Details",
      (value) => !value || value.trim().length > 0,
    )
    .required("This field is required"),
  enhance_assigned_name: yup.string().required("This field is required"),
  enhance_assigned_id: yup.string().required("This field is required"),
  enhancement_status: yup.string().required("This field is required"),
  release_date: yup.string(),
  work_started_on:yup.string(),
  remarks: yup.string(),
});

const AddEnhancements = () => {
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const employeeID = userInfo?.employee_id || "";
  const organizationID = userInfo?.agencies?.id || "";
   const agencyId = parseInt(userInfo?.agencies?.id);

  const [employeeOptions, setEmployeeOptions] = useState<
    { key: string; name: string }[]
  >([]);
  const [extAgenciesLoading, setExtAgenciesLoading] = useState(false);
  const [extAgencies, setExtAgencies] = useState<any>([]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    trigger,
    watch,
    clearErrors,
    control,
  } = useForm<any>({
    resolver: yupResolver(addEnhancementSchema),
    defaultValues: {
      enhancement_status: "Open",
      other_platform: "",
    },
  });

  // Fetch active agencies from external Supabase when meeting type is Client Meeting
  const fetchExtAgencies = useCallback(async () => {
    setExtAgenciesLoading(true);
    try {
      const { data, error } = await getSupabaseExt()
        .from("companies")
        .select("agency_id, name, status")
        .eq("status", "Active");
      if (!error && data) {
        setExtAgencies(
          data.map((row: any) => ({
            id: String(row.agency_id),
            name: row.name,
          })),
        );
      }
    } finally {
      setExtAgenciesLoading(false);
    }
  }, []);

  // API
  const [
    getEmployeesByAgencyID,
    { data: employeesData, loading: employeesDataLoading },
  ] = useLazyQuery(EMPLOYEES_BY_AGENCY_ID, { fetchPolicy: "no-cache" });

  const [create_enhancement, { loading: createEnhanceLoading }] = useMutation(CREATE_ENHANCEMENT, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (organizationID) {
      getEmployeesByAgencyID({
        variables: { agency_id: parseInt(organizationID) },
      });
    }
  }, [organizationID]);

  useEffect(() => {
    if (employeesData?.employees_by_agency_id) {
      const opts = employeesData.employees_by_agency_id.employees.map(
        (emp: any) => ({
          key: String(emp.employee_id),
          name: `${emp.first_name} ${emp.last_name}`,
        }),
      );
      setEmployeeOptions(opts);
    }
  }, [employeesData]);

  useEffect(() => {
    fetchExtAgencies();
  }, []);

  // Watched values for controlled dropdowns
  const watchReportedByName = watch("reported_by_name");
  const watchEnhancementAssignedName = watch("enhance_assigned_name");
  const watchPlatform = watch("platform");
  const watchPriority = watch("priority");
  const watchEnhancementStatus = watch("enhancement_status");

  function handleSelectReportedBy(key: any, name: string) {
    setValue("reported_by_id", String(key));
    setValue("reported_by_name", name);
    trigger("reported_by_name");
  }

  function handleSelectEnhancementAssigned(key: any, name: string) {
    setValue("enhance_assigned_id", String(key));
    setValue("enhance_assigned_name", name);
    trigger("enhance_assigned_name");
  }
  function handleSelectPlatform(key: any, name: string) {
    setValue("platform", name);

    if (name === "Other") {
      setValue("other_platform", "");
      clearErrors("other_platform"); // Hide error until user interacts
    } else {
      setValue("other_platform", "");
      clearErrors("other_platform");
    }

    trigger("platform");
  }

  function handleSelectPriority(key: any, name: string) {
    setValue("priority", name);
    trigger("priority");
  }

  function handleSelectEnhancementStatus(key: any, name: string) {
    setValue("enhancement_status", name);
    trigger("enhancement_status");
  }

  async function onSubmit(data: AddEnhancementFormData) {
    const input: any = {
      enhancement_reported_on: data.enhancement_reported_on,
      reported_by: parseInt(data.reported_by_id),
      agency_name: data.agency_name,
      platform: data.platform === "Other" ? data.other_platform : data.platform,
      priority: data.priority,
      enhancement_details: data.enhancement_details,
      enhancement_assigned: parseInt(data.enhance_assigned_id),
      enhancement_status: data.enhancement_status,
      release_date: data.release_date || null,
      work_started_on:data.work_started_on || null,
      remarks: data.remarks || "",
      created_by: parseInt(employeeID),
      agency_id:agencyId
    };

    try {
      const response = await create_enhancement({ variables: { input } });
      if (response.data.create_enhancement.status_code === 201) {
        showSuccess("Enhancement created successfully");
        navigate(-1);
      } else if (response.data.create_enhancement.status_code === 400) {
        showError(response.data.create_enhancement.message || "Failed to create enhancement");
      }
    } catch (error) {
      if (error instanceof ApolloError) {
        showError(error.message);
      }
    }
  }

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        id="cts-respon-p-dv"
        className="w-full relative overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4 box-border text-left text-base text-gray font-manrope"
      >
        <div className="flex-1 flex flex-col items-start justify-start gap-6">
          <BreadCumb />
          <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
            <img
              className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
              alt=""
              src={backArrow}
              onClick={() => navigate(-1)}
            />
            <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">
              Add Enhancement
            </h3>
          </div>

          <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
            {/* Row 1: Enhancement Date, Platform, Priority, Status */}
            <div className="grid grid-cols-4 mq1400:grid-cols-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 gap-[8px] min-w-full">
              <Controller
                name="enhancement_reported_on"
                control={control}
                render={({ field }) => (
                  <DateSelector
                    label="Enhancement Reported Date*"
                    selectedDate={field.value}
                    onChange={(date) => {
                      field.onChange(date);
                    }}
                    nothideParsedValue={true}
                    blueIcon={true}
                  />
                )}
              />
              <SearchableInputsDropdown
                label="Platform*"
                initialValue={watchPlatform}
                placeHolder="Select Platform"
                options={platformOptions}
                onSelect={handleSelectPlatform}
                errorMessage={errors?.platform}
              />

              {watchPlatform === "Other" && (
                <InputsNew
                  type="text"
                  label="Platform Name*"
                  register={register("other_platform")}
                  errorMessage={errors?.other_platform}
                  contentPlaceholder="Enter Platform Name"
                />
              )}

              <SearchableInputsDropdown
                label="Priority*"
                initialValue={watchPriority}
                placeHolder="Select Priority"
                options={priorityOptions}
                onSelect={handleSelectPriority}
                errorMessage={errors?.priority}
              />
              <SearchableInputsDropdown
                label="Enhancement Status*"
                initialValue={watchEnhancementStatus}
                placeHolder="Select Status"
                options={enhancementStatusOptions}
                onSelect={handleSelectEnhancementStatus}
                errorMessage={errors?.enhancement_status}
              />
            </div>

            {/* Row 2: Reported By, Enhancement Assigned, Agency Name, Release Date */}
            <div className="grid grid-cols-4 mq1400:grid-cols-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 gap-[8px] min-w-full">
              <SearchableInputsDropdown
                label="Reported By*"
                initialValue={watchReportedByName}
                placeHolder="Select Employee"
                options={employeeOptions}
                onSelect={handleSelectReportedBy}
                errorMessage={errors?.reported_by_name}
              />
              <SearchableInputsDropdown
                label="Enhancement Assigned*"
                initialValue={watchEnhancementAssignedName}
                placeHolder="Select Employee"
                options={employeeOptions}
                onSelect={handleSelectEnhancementAssigned}
                errorMessage={errors?.enhance_assigned_name}
              />

              <SearchableInputsDropdown
                label="Agency Name*"
                placeHolder="Agency Name*"
                options={extAgencies}
                onSelect={(id: any,name:any) => {
                  setValue("agency_name", name);
                  trigger("agency_name")
                }}
                errorMessage={errors?.agency_name}
              />
                 <Controller
                name="work_started_on"
                control={control}
                render={({ field }) => (
                  <DateSelector
                    label="Work Started On"
                    selectedDate={field.value}
                    onChange={(date) => {
                      field.onChange(date);
                    }}
                    nothideParsedValue={true}
                    blueIcon={true}
                  />
                )}
              />
              <Controller
                name="release_date"
                control={control}
                render={({ field }) => (
                  <DateSelector
                    label="Release Date"
                    selectedDate={field.value}
                    onChange={(date) => {
                      field.onChange(date);
                    }}
                    nothideParsedValue={true}
                    blueIcon={true}
                  />
                )}
              />
            </div>

            {/* Enhancement Details */}
            <div className="self-stretch flex flex-col items-start justify-start text-sm">
              <div className="self-stretch rounded-radius-md overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)]">
                <InputsNew
                  label="Enhancement Details*"
                  type="textarea"
                  register={register("enhancement_details")}
                  errorMessage={errors.enhancement_details}
                  contentPlaceholder="Describe the enhancement in detail..."
                />
              </div>
            </div>

            {/* Remarks */}
            <div className="self-stretch flex flex-col items-start justify-start text-sm">
              <div className="self-stretch rounded-radius-md overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)]">
                <InputsNew
                  label="Remarks"
                  type="textarea"
                  register={register("remarks")}
                  errorMessage={errors.remarks}
                  contentPlaceholder="Any additional remarks..."
                />
              </div>
            </div>

            {/* Actions */}
            <div className="self-stretch flex flex-row items-center justify-end py-0 pr-0 gap-[8px] mq950:box-border mq1425:flex-wrap mq1425:box-border mq450:box-border">
              <button
                className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
                type="button"
                onClick={() => navigate(-1)}
              >
                <div className="relative text-sm font-body-xs-semi-bold  dark:text-white text-center inline-block">
                  Cancel
                </div>
              </button>
              <button
                className="cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue"
                type="submit"
              >
                <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">
                  Save
                </div>
              </button>
            </div>
          </div>
        </div>
      </form>

      {(employeesDataLoading || createEnhanceLoading) && <Loader />}
    </>
  );
};

export default AddEnhancements;
